'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

export interface HoldToSubmitProps {
  /** Fired exactly once when the user has held for ``holdMs`` milliseconds. */
  onSubmit: () => void;
  /** Visible label. Falls back to "Hold to submit". */
  label?: string;
  /** Optional override for the hold duration. Defaults to 1 second. */
  holdMs?: number;
  /** When true the control ignores all interaction and shows the disabled style. */
  disabled?: boolean;
  /** Optional extra classes for the outer button. */
  className?: string;
}

/**
 * Press-and-hold submit control.
 *
 * Replaces a click-to-submit primary button with a deliberate hold gesture
 * so it's harder to commit irrevocable actions (e.g. PA answers DoseSpot
 * won't let us revise) by accident. Releasing or moving the cursor off the
 * button before the threshold cancels.
 */
export function HoldToSubmit({
  onSubmit,
  label = 'Hold to submit',
  holdMs = 1000,
  disabled = false,
  className,
}: HoldToSubmitProps) {
  const [holding, setHolding] = useState(false);
  const completionTimer = useRef<number | null>(null);
  // ``onSubmit`` is captured into a ref so the timer always fires the
  // *latest* callback, even if the parent re-renders mid-hold.
  const onSubmitRef = useRef(onSubmit);
  useEffect(() => {
    onSubmitRef.current = onSubmit;
  }, [onSubmit]);

  const cancel = useCallback(() => {
    if (completionTimer.current !== null) {
      window.clearTimeout(completionTimer.current);
      completionTimer.current = null;
    }
    setHolding(false);
  }, []);

  const begin = useCallback(() => {
    if (disabled) return;
    if (completionTimer.current !== null) return;
    setHolding(true);
    completionTimer.current = window.setTimeout(() => {
      completionTimer.current = null;
      setHolding(false);
      onSubmitRef.current();
    }, holdMs);
  }, [disabled, holdMs]);

  // Make sure we don't leak a timer if the button unmounts mid-hold.
  useEffect(() => cancel, [cancel]);

  // Adjust internal hold state when the parent flips ``disabled`` mid-hold.
  // React's recommended pattern (https://react.dev/learn/you-might-not-need-an-effect)
  // for "reset state when a prop changes" is to compare against the previous
  // prop in render and reset inline -- this avoids the wasted commit a
  // useEffect would impose, and is what react-hooks/set-state-in-effect
  // pushes us toward. The timer itself is an imperative side effect, so its
  // cleanup stays in the effect below.
  const [previousDisabled, setPreviousDisabled] = useState(disabled);
  if (disabled !== previousDisabled) {
    setPreviousDisabled(disabled);
    if (disabled) {
      setHolding(false);
    }
  }

  useEffect(() => {
    if (disabled && completionTimer.current !== null) {
      window.clearTimeout(completionTimer.current);
      completionTimer.current = null;
    }
  }, [disabled]);

  return (
    <button
      type="button"
      data-state={holding ? 'holding' : 'idle'}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        begin();
      }}
      onPointerUp={cancel}
      onPointerCancel={cancel}
      onPointerLeave={cancel}
      onBlur={cancel}
      onContextMenu={(event) => event.preventDefault()}
      className={cn(
        // No hover/active bg-shift — those would visually compete with
        // (and on some themes hide) the fill animation, which is the
        // whole point of this control. Cursor still flips to "pointer"
        // so the button feels alive.
        'relative inline-flex h-8 select-none items-center justify-center overflow-hidden rounded-md border border-input bg-primary px-3 text-xs font-medium text-primary-foreground shadow-xs outline-none',
        'focus-visible:ring-ring/50 focus-visible:ring-[3px]',
        'active:bg-primary',
        'disabled:cursor-not-allowed disabled:opacity-50',
        !disabled && 'cursor-pointer',
        className
      )}
      style={{
        touchAction: 'none',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <span
        aria-hidden
        // Width-based fill rather than transform: scaleX. The latter
        // depends on Tailwind's ``--tw-scale-x`` CSS variable plumbing,
        // which can silently fail to animate when other transform
        // utilities (or third-party CSS) interfere. Width is bullet-proof.
        // Fill colour = primary-foreground, which is by definition the
        // contrasting colour to the button bg in either theme: dark on a
        // light/white primary (dark mode) and light on a dark primary
        // (light mode). 40% opacity keeps the label readable through it.
        className="pointer-events-none absolute inset-y-0 left-0 z-0 bg-primary-foreground/40"
        style={{
          width: holding ? '100%' : '0%',
          transition: `width ${holding ? holdMs : 150}ms ${holding ? 'linear' : 'ease-out'}`,
        }}
      />
      <span className="relative z-[1]">{label}</span>
    </button>
  );
}
